/**
 * Ask the photo CDN for the size we actually render, instead of the size it defaults to.
 *
 * ## Why
 *
 * Images are 66% of the homepage's weight — 4,195 KB of 6,372 KB (123yb6937gx). Almost none of
 * that is our own imagery: it is Unsplash and Pexels URLs stored in the explore documents, which
 * carry the CDN's own default width (`w=1080` on Unsplash, up to 1880 px on Pexels) and get
 * rendered into cards 208–400 px wide. The bytes are thrown away in the browser after being paid
 * for on the wire.
 *
 * Both CDNs resize from query parameters, so this is a string rewrite and not a pipeline: no
 * download, no re-host, no build step. `sizedPhoto(url, 416)` is the whole fix for a given site.
 *
 * ## What is deliberately preserved
 *
 * Unsplash's `ixid` and `ixlib` are attribution/telemetry parameters tied to the API response the
 * URL came from. They are part of how Unsplash counts a view, and their licence guidance expects
 * them to survive. This rewrites `w`/`h`/`fm`/`q` and leaves everything else exactly as found —
 * which is also why it edits the existing query rather than rebuilding the URL from the photo id
 * the way the local `ux()` helpers in TrendingDestinationsSection and CommunitySection do. Those
 * build a URL from a hardcoded id for the avatar strip; this one takes a URL that came from data.
 *
 * ## Wikimedia (curator photo picks)
 *
 * A curator's pick from Wikimedia Commons is stored, and was served, as the ORIGINAL upload —
 * `upload.wikimedia.org/wikipedia/commons/c/c7/Trevi_Fountain_-_Roma.jpg`. Every web surface
 * rendered that file: an 80x80 trip-board card downloaded 26.2 MB for Trevi Fountain and 8.9 MB
 * for the Rijksmuseum, with a grey placeholder until it arrived (QA on #1359, 2026-10-05).
 *
 * Wikimedia resizes by path, not by query: the original `/wikipedia/<project>/<a>/<ab>/<File>`
 * has a thumbnail at `/wikipedia/<project>/thumb/<a>/<ab>/<File>/<W>px-<File>`. Measured against
 * the live thumbnailer for Trevi: 250 px = 15 KB, 500 px = 48 KB, 960 px = 149 KB, 1280 px =
 * 252 KB, original = 26.2 MB. Both `upload.wikimedia.org` and `thumb.wikimedia.org` (same layout) are handled.
 *
 * Two constraints shape the code:
 * - Only the standard widths are served (120, 250, 330, 500, 960, 1280, 1920, 3840). An arbitrary
 *   width such as 300 is an HTTP 400, so the requested width snaps UP to the next standard one,
 *   capped at 3840. Snapping up is safe even for a tiny original: a 135 px-wide file asked at 250
 *   or 960 comes back 200, upscaled. The one exception is a standard width at most 5% short of the
 *   request, which is taken instead: a 254 px cover thumb gets 250, not 330, and its 2x 500, not 960.
 *   A 2% upscale is invisible, while the next step up can triple the bytes (Trevi: 500 px = 48 KB,
 *   960 px = 149 KB).
 * - Only raster files (jpg, jpeg, png, gif, webp) keep their file name in the thumbnail path. SVG,
 *   TIFF, PDF, audio, `/math/` renders and anything under `archive/` get a different thumbnail
 *   name (or none), so they, and any path that is not exactly the shape above, come back
 *   unchanged rather than being guessed at.
 *
 * The query string (`?utm_content=original`, `?utm_source=...`) and fragment are kept exactly as
 * found, and the path is edited as raw text rather than through `URL`, so a percent-encoded file
 * name is not re-encoded on the way through.
 *
 * ## Cover slots: size for the height, not the width
 *
 * `width` is the CSS width of the slot, and for a slot that is wider than the photo is tall that is
 * the right thing to ask for. In an `object-fit: cover` slot that is TALLER than the photo's aspect
 * (a square thumbnail, a portrait card) it is not: the browser scales a landscape photo until it
 * fills the slot's HEIGHT and crops the sides, so the file needed is `height * aspect` wide, not
 * `width`. Asking for `width` serves a file the browser then upscales (123yb697pu8).
 * Measured needed/served for the widths this used to pass: 160x202 list card 2.27x, 111x231 hotel
 * card 2.59x, 54x54 day row 1.66x, 80x80 trip-board card 1.18x. `coverSlotWidth` does the sum.
 *
 * ## Render time only
 *
 * This belongs at the `<img src>`, never in a comparison. Whether a photo is "the display image"
 * (credit badge, de-duplication, `validImages[0] === displayImage`) is decided on the URL as
 * stored; sizing it first would make the same photo look like a different one.
 *
 * ## What it will not touch
 *
 * Anything that is not Unsplash, Pexels or a Wikimedia raster original/thumbnail comes back
 * byte-identical: our own origin, the flag CDNs, an already-sized URL from `cities.ts`, a relative
 * path, an empty string. A helper that mangles an unknown host is worse than one that does
 * nothing, because the failure is a broken image rather than a large one.
 */

/** Widths we ask for, doubled for the 2x descriptor. Kept small and explicit — a CDN cache is per
 *  exact URL, so an arbitrary width per call would fragment it and miss on every new viewport. */
const MAX_DPR_WIDTH = 2;

/** The widths Wikimedia's thumbnailer serves; anything else is an HTTP 400. */
const WIKIMEDIA_WIDTHS = [120, 250, 330, 500, 960, 1280, 1920, 3840] as const;

// thumb.wikimedia.org is where Commons imageinfo thumbnail URLs point; same path layout.
const WIKIMEDIA_HOSTS = new Set(["upload.wikimedia.org", "thumb.wikimedia.org"]);
const WIKIMEDIA_RASTER = /\.(?:jpe?g|png|gif|webp)$/i;
const WIKIMEDIA_THUMB_NAME = /^(\d+)px-(.+)$/;
const WIKIMEDIA_PROJECT = /^[a-z0-9-]+$/i;

/** How far short of the requested width a standard width may fall and still be taken (see the header). */
const WIKIMEDIA_SNAP_TOLERANCE = 0.05;

/**
 * The standard width to serve for `width`: the largest one below it if that is within
 * WIKIMEDIA_SNAP_TOLERANCE, otherwise the smallest one at least as wide, capped at the largest.
 */
function snapWikimediaWidth(width: number): number {
	const needed = Math.ceil(width);
	const below = [...WIKIMEDIA_WIDTHS].reverse().find((w) => w < needed);
	if (below !== undefined && below >= needed * (1 - WIKIMEDIA_SNAP_TOLERANCE)) return below;
	return WIKIMEDIA_WIDTHS.find((w) => w >= needed) ?? WIKIMEDIA_WIDTHS[WIKIMEDIA_WIDTHS.length - 1];
}

/** Two path segments name the same file; tolerant of one side being percent-encoded and not the other. */
function sameFileName(a: string, b: string): boolean {
	if (a === b) return true;
	try {
		return decodeURIComponent(a) === decodeURIComponent(b);
	} catch {
		return false;
	}
}

/**
 * The thumbnail of a Wikimedia raster original (or the same thumbnail re-snapped to another width),
 * or null when the URL is not exactly one of those two shapes. Operates on the raw text so the
 * query, fragment and any percent-encoding survive untouched.
 */
function rewriteWikimedia(url: string, width: number): string | null {
	if (!Number.isFinite(width) || width <= 0) return null;

	// scheme://host | path | query+fragment
	const parts = /^https?:\/\/[^/?#]+([^?#]*)(.*)$/i.exec(url);
	if (!parts) return null;
	const [, rawPath, rest] = parts;
	const prefix = url.slice(0, url.length - rawPath.length - rest.length);

	// rawPath starts with "/", so the first segment is always "". A trailing slash adds an empty
	// segment and changes the count, which is what rejects it.
	const seg = rawPath.split("/");
	if (seg[0] !== "" || seg[1] !== "wikipedia" || !WIKIMEDIA_PROJECT.test(seg[2] ?? "")) return null;

	let project: string;
	let a: string;
	let ab: string;
	let file: string;
	if (seg.length === 6) {
		// Original: /wikipedia/<project>/<a>/<ab>/<File>
		[, , project, a, ab, file] = seg;
	} else if (seg.length === 8 && seg[3] === "thumb") {
		// Thumbnail: /wikipedia/<project>/thumb/<a>/<ab>/<File>/<W>px-<File>
		[, , project, , a, ab, file] = seg;
		const thumb = WIKIMEDIA_THUMB_NAME.exec(seg[7]);
		if (!thumb || !sameFileName(thumb[2], file)) return null;
	} else {
		return null;
	}

	// The two directory levels are the first one and two hex digits of the file name's MD5.
	if (!/^[0-9a-f]$/.test(a) || !/^[0-9a-f]{2}$/.test(ab) || ab[0] !== a) return null;
	if (!file || !WIKIMEDIA_RASTER.test(file)) return null;

	const target = snapWikimediaWidth(width);
	return `${prefix}/wikipedia/${project}/thumb/${a}/${ab}/${file}/${target}px-${file}${rest}`;
}

function rewriteUnsplashPexels(parsed: URL, isUnsplash: boolean, width: number): string {
	const p = parsed.searchParams;
	p.set("w", String(Math.round(width)));

	// A stored `h` was chosen for the stored `w`. Keeping it while changing `w` asks the CDN for a
	// crop nobody designed — the card's own CSS (object-fit: cover) is what decides the shape, so
	// the height constraint is not needed and an inconsistent one is actively wrong.
	p.delete("h");

	if (isUnsplash) {
		// The stored URLs say `fm=jpg`. WebP is materially smaller at the same quality and every
		// browser that reaches this code supports it (the marketing bundle already ships .webp
		// imagery unconditionally).
		p.set("fm", "webp");
		// Stored value is q=80. 70 is the usual floor before artefacts show on photographic
		// content, and these are decorative card images, not the subject of the page.
		p.set("q", "70");
	}

	parsed.search = p.toString();
	return parsed.toString();
}

/**
 * The resized URL, or null when this is not a photo URL it knows how to resize. Null rather than
 * "the same string back" so a caller can tell "cannot resize" from "already the right size" — a
 * Wikimedia thumbnail at the requested width rewrites to itself.
 */
function rewrite(url: string, width: number): string | null {
	if (!url || typeof url !== "string") return null;
	// Bail before parsing on anything that is not an absolute http(s) URL — `new URL` throws on a
	// relative path, and a data: URI has no query to rewrite.
	if (!/^https?:\/\//i.test(url)) return null;

	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return null;
	}

	const host = parsed.hostname.toLowerCase();
	if (WIKIMEDIA_HOSTS.has(host)) return rewriteWikimedia(url, width);
	const isUnsplash = host === "images.unsplash.com";
	const isPexels = host === "images.pexels.com";
	if (!isUnsplash && !isPexels) return null;
	return rewriteUnsplashPexels(parsed, isUnsplash, width);
}

/** The aspect (width / height) `coverSlotWidth` assumes a photo has. */
const COVER_PHOTO_ASPECT = 2;

/**
 * The width to request for a photo shown in an `object-fit: cover` slot of `width` x `height` CSS
 * px: the slot's own width, or, when the slot is taller than a 2:1 photo, the width the photo ends
 * up at once it is scaled to the slot's height.
 *
 * The photo's real aspect is unknown before it loads, so this assumes 2:1. That covers the wide
 * landmark photos curators pick (Trevi Fountain is ~1.86) without upscaling, and a narrower photo
 * just comes back a little larger than needed. The extra bytes stay small because Wikimedia snaps
 * to its standard widths: Trevi at 960 px is 149 KB, at 330 px 25 KB. For a slot that is wider
 * than 2:1 the slot's width wins and this is a no-op.
 *
 * Pass the result wherever the slot's width went (`sizedPhoto`, `srcSetFor`, and the `probeWidth`
 * of `useValidImages`, so the probe URL equals the `<img>` URL and the render is a cache hit).
 */
export function coverSlotWidth(width: number, height: number): number {
	return Math.max(width, Math.ceil(height * COVER_PHOTO_ASPECT));
}

/**
 * The same photo at the width it is displayed at.
 *
 * `width` is the CSS pixel width of the slot, not the device pixel width — the 2x variant is
 * `srcSetFor`'s job, so passing an already-doubled number here would double it again.
 */
export function sizedPhoto(url: string, width: number): string {
	return rewrite(url, width) ?? url;
}

/**
 * A `srcset` for the same photo at 1x and 2x, or an empty string when the URL is one this cannot
 * resize. An empty string is falsy, so `srcSet={srcSetFor(...) || undefined}` at the call site
 * keeps the attribute off the element entirely rather than emitting `srcset=""`, which some
 * browsers treat as a candidate list containing nothing.
 *
 * Wikimedia only serves standard widths, so 1x and 2x can snap to the same one (a 3000 px slot is
 * 3840 at both). That yields a single `<url> 1x` candidate — valid, and honest about there being
 * nothing larger to offer — rather than the same URL listed twice.
 */
export function srcSetFor(url: string, width: number): string {
	const one = rewrite(url, width);
	if (one === null) return "";
	const two = rewrite(url, width * MAX_DPR_WIDTH);
	if (two === null || two === one) return `${one} 1x`;
	return `${one} 1x, ${two} 2x`;
}
